import { Link } from '../../components/SmartLink';
import {
  CheckCircle, Warning, MapPin, WhatsappLogo,
  Clock, ArrowRight, Shield,
  Users, SealCheck, CalendarBlank,
  FileText, Star, Briefcase, GraduationCap,
} from '@phosphor-icons/react';
import VisaDetailLayout from '../../components/visas/VisaDetailLayout';
import { VISA_BY_ID } from '../../data/visaData';
import { buildWAUrl } from '../../lib/whatsapp';
import { ExpandableTip, AIAnswerBox } from '../../components/visas/VisaPageHelpers';

const WA_URL = buildWAUrl(
  'Hi Big Bird Travel, I want to apply for a UAE Job Seeker Visa. My qualification is ____. Please advise the duration category and cost.'
);

const SUMMARY_CARDS = [
  { label: 'Duration Options', value: '60 / 90 / 120 Days', sub: 'Based on qualification', Icon: CalendarBlank },
  { label: 'Sponsor Needed', value: 'Not Required', sub: 'Enter to search, not to work', Icon: Shield },
  { label: 'Processing Time', value: '2 – 4 Working Days', sub: 'For the entry permit', Icon: Clock },
  { label: 'Who Can Apply', value: 'Graduates & Professionals', sub: 'Skill-based tiers', Icon: Briefcase },
  { label: 'Apply From', value: 'Abroad or Inside UAE', sub: 'Status change possible', Icon: MapPin },
];

const GUIDE_FOR = [
  { Icon: GraduationCap, label: 'Fresh Graduates from Recognised Universities' },
  { Icon: Briefcase,     label: 'Skilled Professionals Between Jobs' },
  { Icon: Star,          label: 'Executives & Specialists' },
  { Icon: Users,         label: 'Candidates Attending UAE Interviews' },
  { Icon: FileText,      label: 'Visit Visa Holders Wanting to Job Search Legally' },
  { Icon: MapPin,        label: 'Candidates Targeting Abu Dhabi or Dubai Roles' },
];

const CATEGORY_TABLE = [
  { category: 'Fresh graduate, entry-level', duration: '60 days', requirement: 'Recognised degree', badge: 'blue' },
  { category: 'Experienced professional', duration: '90 days', requirement: 'Degree + relevant experience', badge: 'blue' },
  { category: 'Executive / specialist', duration: '120 days', requirement: 'Senior role or specialised skill', badge: 'navy' },
];

const HIDDEN_FACTS = [
  {
    Icon: Briefcase,
    title: 'This is not a work visa — it is a search visa',
    desc: 'The Job Seeker Visa lets you enter and interview, but you cannot legally start earning UAE income until an employer sponsors your actual employment visa. Working on the search visa itself is not permitted.',
  },
  {
    Icon: Shield,
    title: 'Duration is decided by your qualification, not your choice',
    desc: 'You can’t simply pick the longest option — the 60/90/120-day tiers are tied to your degree level, experience and role seniority. We assess your CV first so you know your real category before paying.',
  },
  {
    Icon: GraduationCap,
    title: 'Certificate attestation can be requested at short notice',
    desc: 'Some categories require attested educational certificates. If yours are not attested yet, start the process before applying — it can take longer than the visa itself.',
  },
  {
    Icon: Users,
    title: 'A visit visa holder can sometimes switch, avoiding a flight home',
    desc: 'If you are already in the UAE on a visit visa and decide to job hunt seriously, switching your status to a Job Seeker Visa can sometimes be done without exiting the country — worth checking before booking a flight out.',
  },
];

const JOB_SEEKER_SERVICES = [
  { label: 'UAE Visit Visa',        to: '/visas/uae-visit-visa', desc: 'If you need a short visit first' },
  { label: 'Visa Extension',        to: '/visas/visa-extension', desc: 'Extend your search window' },
  { label: 'Family Visa',           to: '/visas/family-visa',    desc: 'Once employed & sponsoring' },
  { label: 'Flights',               to: '/flights',              desc: 'To attend UAE interviews' },
  { label: 'All Visa Services',     to: '/visas',                 desc: 'UAE, Schengen, UK, USA & more' },
  { label: 'Contact Our Team',      to: '/contact',               desc: 'Mussafah, Abu Dhabi office' },
];

const SERVICE_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'UAE Job Seeker Visa Assistance',
  description: 'UAE Job Seeker Visa assistance — 60, 90 and 120-day options for candidates job hunting in Abu Dhabi and Dubai without employer sponsorship. Based in Mussafah, Abu Dhabi.',
  url: 'https://bigbirdtravel.com/visas/job-seeker-visa',
  provider: {
    '@type': 'TravelAgency',
    name: 'Big Bird Travel & Tourism',
    url: 'https://bigbirdtravel.com',
    telephone: '+971561789642',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Al Sawari Street 5, Building No-21, Office No-4, M-13',
      addressLocality: 'Mussafah',
      addressRegion: 'Abu Dhabi',
      addressCountry: 'AE',
    },
    email: 'info@bigbirdtravel.com',
    openingHours: 'Mo-Sa 09:00-18:00',
  },
  areaServed: { '@type': 'Country', name: 'United Arab Emirates' },
  serviceType: 'UAE Job Seeker Visa',
  termsOfService: 'https://bigbirdtravel.com/terms',
};

function JobSeekerVisaExtraSections() {
  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(SERVICE_SCHEMA) }} />

      <section className="py-10 px-6 lg:px-8 bg-[#F5FAFF] border-b border-[#D8EAF7]">
        <div className="max-w-7xl mx-auto">
          <p className="text-[11px] font-black uppercase tracking-[0.3em] text-[#0068B3] mb-4">UAE Job Seeker Visa — At a Glance</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {SUMMARY_CARDS.map(({ label, value, sub, Icon }) => (
              <div key={label} className="rounded-2xl border border-[#C3DCF5] bg-white p-4 shadow-sm flex flex-col gap-2">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#EAF7FF] border border-[#C3DCF5]">
                  <Icon size={16} weight="fill" className="text-[#0068B3]" />
                </span>
                <p className="text-[10px] font-black uppercase tracking-wider text-[#0068B3]">{label}</p>
                <p className="font-display font-black text-[#071126] text-[14px] leading-tight">{value}</p>
                <p className="text-[11px] text-gray-400 leading-tight">{sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-10 px-6 lg:px-8 bg-white border-b border-gray-50">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-8 items-start">
          <div className="shrink-0 lg:w-60">
            <span className="text-[11px] font-black uppercase tracking-[0.3em] text-[#0068B3] mb-2 block">About This Guide</span>
            <h2 className="font-display font-black text-[#071126] text-[20px] leading-tight">Who this is for</h2>
            <p className="text-gray-500 text-[13px] mt-2 leading-relaxed">
              For candidates targeting roles in Abu Dhabi or Dubai who want to search and interview in person before committing to a move.
            </p>
          </div>
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {GUIDE_FOR.map(({ Icon, label }) => (
              <div key={label} className="flex items-center gap-3 rounded-xl border border-[#D8EAF7] bg-[#F5FAFF] px-4 py-3">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#EAF7FF] border border-[#C3DCF5]">
                  <Icon size={14} weight="fill" className="text-[#0068B3]" />
                </span>
                <span className="text-[13px] font-semibold text-[#071126]">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8 space-y-4">
        <AIAnswerBox
          question="What is the UAE Job Seeker Visa and how long does it last?"
          answer="It is a visa that lets candidates enter the UAE specifically to search for work and attend interviews in person, without needing an employer to sponsor their entry first. Validity is commonly 60, 90 or 120 days depending on the candidate's qualification and experience level. Big Bird Travel, based in Mussafah, Abu Dhabi, assesses a candidate's CV to confirm the correct duration category before applying."
        />
        <AIAnswerBox
          question="Can I convert a Job Seeker Visa into a work visa?"
          answer="Yes — if you secure a job offer during the validity period, your new employer typically sponsors your employment visa, and the process is often smoother since you are already inside the UAE with valid status. You cannot, however, legally earn income while still on the Job Seeker Visa itself."
        />
      </div>

      <section className="py-12 px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <span className="text-[11px] font-black uppercase tracking-[0.3em] text-[#0068B3] mb-3 block">Duration by Qualification</span>
          <h2 className="font-display font-black text-[#071126] mb-2" style={{ fontSize: 'clamp(1.3rem, 2vw, 1.65rem)' }}>
            Job Seeker Visa categories — 2026
          </h2>
          <p className="text-gray-500 text-[13px] mb-6 max-w-2xl">
            Exact classification is decided during application review — this is a general guide. Confirm your category with us before applying.
          </p>
          <div className="overflow-x-auto rounded-2xl border border-[#D8EAF7] shadow-sm">
            <table className="w-full text-left text-[13px] min-w-[600px]">
              <thead>
                <tr className="bg-[#071126] text-white">
                  <th className="px-5 py-3.5 font-black text-[11px] uppercase tracking-wider rounded-tl-2xl">Candidate Profile</th>
                  <th className="px-5 py-3.5 font-black text-[11px] uppercase tracking-wider">Typical Duration</th>
                  <th className="px-5 py-3.5 font-black text-[11px] uppercase tracking-wider rounded-tr-2xl">Requirement</th>
                </tr>
              </thead>
              <tbody>
                {CATEGORY_TABLE.map((row, i) => (
                  <tr key={row.category} className={`border-t border-[#E8F3FC] ${i % 2 === 0 ? 'bg-white' : 'bg-[#F5FAFF]'}`}>
                    <td className="px-5 py-3.5 font-semibold text-[#071126]">{row.category}</td>
                    <td className="px-5 py-3.5">
                      <span className={`inline-block rounded-full px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wide whitespace-nowrap ${
                        row.badge === 'navy' ? 'bg-[#071126] text-white' : 'bg-[#EAF7FF] text-[#0068B3]'
                      }`}>{row.duration}</span>
                    </td>
                    <td className="px-5 py-3.5 text-gray-600">{row.requirement}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="py-14 px-6 lg:px-8 bg-amber-50 border-y border-amber-100">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-10 items-start">
          <div className="lg:col-span-2">
            <span className="text-[11px] font-black uppercase tracking-[0.3em] text-amber-700 mb-3 block">⚠ Know Before You Apply</span>
            <h2 className="font-display font-black text-[#071126] mb-4" style={{ fontSize: 'clamp(1.4rem, 2.2vw, 1.85rem)' }}>
              Make the most of your job-search window
            </h2>
            <p className="text-gray-700 text-[15px] leading-relaxed mb-4">
              The visa gives you time in the country — it doesn't guarantee a job. Candidates who line up interviews before arriving, and keep documents attested in advance, get far more out of the window.
            </p>
            <div className="space-y-2.5 mt-6">
              <ExpandableTip title="Attest your certificates before you travel">
                If your category requires attested educational certificates, get this done in your home country before applying — it is usually faster there than arranging it after arrival.
              </ExpandableTip>
              <ExpandableTip title="Line up interviews before you land">
                The visa lets you search in person, but arriving with even a few interviews scheduled makes the limited window far more productive than starting from zero.
              </ExpandableTip>
              <ExpandableTip title="You cannot legally work on this visa">
                Any paid work during the Job Seeker Visa period — even informal or remote — is not permitted under this status. Wait for the employment visa once an offer is confirmed.
              </ExpandableTip>
              <ExpandableTip title="Check status-change options if you're already on a visit visa">
                If you're already in the UAE and decide to job search seriously, ask us whether your current visa can switch to Job Seeker status without exiting.
              </ExpandableTip>
            </div>
          </div>

          <div className="bg-white border border-amber-200 rounded-2xl p-6 shadow-sm lg:sticky lg:top-24">
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle size={18} weight="fill" className="text-[#0068B3]" />
              <span className="font-black text-[12px] uppercase tracking-widest text-[#0068B3]">Quick Eligibility Check</span>
            </div>
            <ul className="space-y-3 mb-5">
              {[
                { text: 'Recent graduate, recognised degree', result: '60-day category', type: 'ok' },
                { text: 'Degree + relevant experience', result: '90-day category', type: 'ok' },
                { text: 'Senior / executive profile', result: '120-day category', type: 'ok' },
                { text: 'Certificates not attested yet', result: 'Start this before applying', type: 'warn' },
              ].map(item => (
                <li key={item.text} className="flex items-start gap-2.5">
                  {item.type === 'ok' && <CheckCircle size={14} weight="fill" className="text-green-500 shrink-0 mt-0.5" />}
                  {item.type === 'warn' && <Warning size={14} weight="fill" className="text-amber-500 shrink-0 mt-0.5" />}
                  <span className="text-[12px] text-gray-700"><strong>{item.text}</strong> → {item.result}</span>
                </li>
              ))}
            </ul>
            <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 w-full rounded-xl bg-[#0F7A35] text-white text-[13px] font-black py-3.5 hover:bg-[#0a6029] transition-colors">
              <WhatsappLogo size={17} weight="fill" /> Check My Category — Free
            </a>
            <p className="text-center text-[11px] text-gray-400 mt-2.5 leading-snug">
              Send your CV summary — honest answer, same day.
            </p>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden py-14 px-6 lg:px-8 bg-[#071126] text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(0,150,255,0.10),transparent_55%)] pointer-events-none" />
        <div className="relative z-10 max-w-7xl mx-auto">
          <span className="text-[11px] font-black uppercase tracking-[0.3em] text-[#C9A227] mb-3 block">Authority Section</span>
          <h2 className="font-display font-black text-white mb-3" style={{ fontSize: 'clamp(1.4rem, 2.2vw, 1.85rem)' }}>
            What most job-seeker visa pages don't tell you
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {HIDDEN_FACTS.map(({ Icon, title, desc }) => (
              <div key={title} className="rounded-2xl border border-[#0096FF]/20 bg-white/[0.045] p-5 hover:border-[#0096FF]/40 hover:bg-white/[0.06] transition-all">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#0096FF]/10 border border-[#0096FF]/20 mb-4">
                  <Icon size={17} weight="fill" className="text-[#0096FF]" />
                </span>
                <h3 className="font-display font-black text-white text-[13px] mb-2 leading-tight">{title}</h3>
                <p className="text-blue-50/60 text-[12px] leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 rounded-2xl border border-[#0096FF]/25 bg-white/[0.04] p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex-1">
              <p className="font-display font-black text-white text-[15px] mb-1">Planning a job search trip to the UAE?</p>
              <p className="text-blue-50/60 text-[13px]">Send your qualification and experience. We'll confirm your duration category and an honest cost — usually within the hour.</p>
            </div>
            <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="shrink-0 inline-flex items-center gap-2 bg-[#0F7A35] text-white text-[13px] font-black px-5 py-3 rounded-xl hover:bg-[#0a6029] transition-colors">
              <WhatsappLogo size={17} weight="fill" /> Ask on WhatsApp
            </a>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden py-14 px-6 lg:px-8 bg-[#041024] text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(0,150,255,0.14),transparent_50%)]" />
        <div className="relative z-10 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-[11px] font-black uppercase tracking-[0.3em] text-[#0096FF] mb-3 block">Why Big Bird</span>
            <h2 className="font-display font-black text-white mb-4" style={{ fontSize: 'clamp(1.4rem, 2.2vw, 1.85rem)' }}>
              Job search visa, handled from CV to entry permit
            </h2>
            <p className="text-blue-50/85 text-[15px] italic leading-relaxed mb-5 border-l-2 border-[#0096FF] pl-4">
              "Send your CV. We'll confirm your duration category and prepare the entry permit so you can focus on interviews, not paperwork."
            </p>
            <p className="text-blue-50/70 text-[14px] leading-relaxed mb-4">
              We're a licensed travel agency in <strong className="text-white">Mussafah, Abu Dhabi</strong>. For Job Seeker Visas we:
            </p>
            <ul className="space-y-3 mb-6">
              {[
                'Assess your CV to confirm the correct duration category.',
                'Guide certificate attestation where required.',
                'Prepare and submit the entry permit application.',
                'Advise on status change if you convert to an employment visa.',
                'Support candidates applying from abroad or already inside the UAE.',
              ].map(item => (
                <li key={item} className="flex items-start gap-3 text-[13px] text-blue-50/80">
                  <CheckCircle size={14} weight="fill" className="text-[#0096FF] shrink-0 mt-0.5" />
                  {item}
                </li>
              ))}
            </ul>
            <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#0F7A35] text-white text-[13px] font-black px-6 py-3.5 rounded-xl hover:bg-[#0a6029] transition-colors">
              <WhatsappLogo size={19} weight="fill" /> WhatsApp Us — Same-Day Response
            </a>
          </div>
          <div className="space-y-4">
            <div className="bg-white/[0.05] border border-[#0096FF]/20 rounded-3xl p-6">
              <p className="text-[10px] font-black uppercase tracking-widest text-[#0096FF]/70 mb-4">Big Bird Travel — Agency Details</p>
              <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                {[
                  { label: 'Office',   value: 'Mussafah 13, Abu Dhabi' },
                  { label: 'WhatsApp', value: '+971 56 178 9642' },
                  { label: 'License',  value: 'ADRA CN-5285999' },
                  { label: 'Response', value: 'Same day' },
                  { label: 'Hours',    value: 'Mon–Sat 9am–6pm UAE' },
                  { label: 'Serves',   value: 'Abu Dhabi · Dubai · UAE' },
                ].map(item => (
                  <div key={item.label}>
                    <p className="text-[10px] font-black uppercase tracking-widest text-[#0096FF]/60 mb-0.5">{item.label}</p>
                    <p className="text-[13px] font-semibold text-white">{item.value}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white/[0.05] border border-[#0096FF]/20 rounded-2xl p-5">
              <div className="flex items-center gap-1 mb-2">
                {[...Array(5)].map((_, i) => (<Star key={i} size={12} weight="fill" className="text-[#C9A227]" />))}
                <span className="text-[11px] text-blue-50/50 ml-2">Verified customer</span>
              </div>
              <blockquote className="text-[13px] text-blue-50/80 italic leading-relaxed">
                "They confirmed my 90-day category quickly and the visa was ready before my flight. I landed and went straight into interviews."
              </blockquote>
              <p className="text-[11px] text-blue-50/40 mt-2">— Job seeker, relocated to Abu Dhabi</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 px-6 lg:px-8 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto">
          <span className="text-[11px] font-black uppercase tracking-[0.3em] text-[#0068B3] mb-3 block">Related Visa Services</span>
          <h2 className="font-display font-black text-[#071126] mb-6 text-[20px]">
            Job search sorted. Here's what comes next.
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            {JOB_SEEKER_SERVICES.map(({ label, to, desc }) => (
              <Link key={label} to={to} className="group rounded-2xl border border-[#D8EAF7] bg-[#F5FAFF] p-4 hover:border-[#0096FF]/50 hover:bg-[#EAF7FF] hover:-translate-y-0.5 transition-all flex flex-col gap-2">
                <ArrowRight size={15} weight="bold" className="text-[#0068B3] group-hover:translate-x-0.5 transition-transform" />
                <p className="font-display font-black text-[#071126] text-[13px] leading-tight">{label}</p>
                <p className="text-[11px] text-gray-400">{desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-7 px-6 lg:px-8 bg-[#F5FAFF] border-b border-[#D8EAF7]">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center gap-x-8 gap-y-3">
          <div className="flex items-center gap-2 text-[12px] text-gray-500"><CalendarBlank size={13} weight="fill" className="text-[#0068B3]" /><span><strong className="text-[#071126]">Last Updated:</strong> September 2026</span></div>
          <div className="flex items-center gap-2 text-[12px] text-gray-500"><SealCheck size={13} weight="fill" className="text-[#0068B3]" /><span><strong className="text-[#071126]">Reviewed by:</strong> Big Bird Travel Visa Team</span></div>
          <div className="flex items-center gap-2 text-[12px] text-gray-500"><Shield size={13} weight="fill" className="text-[#0068B3]" /><span><strong className="text-[#071126]">License:</strong> ADRA CN-5285999</span></div>
          <div className="flex items-center gap-2 text-[12px] text-gray-500"><MapPin size={13} weight="fill" className="text-[#0068B3]" /><span><strong className="text-[#071126]">Office:</strong> Mussafah 13, Abu Dhabi, UAE</span></div>
          <div className="flex items-center gap-2 text-[12px] text-gray-500"><WhatsappLogo size={13} weight="fill" className="text-green-600" /><span><strong className="text-[#071126]">WhatsApp:</strong> Same-day response</span></div>
        </div>
      </section>

      <section className="py-12 px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-6 items-start">
            <div className="shrink-0">
              <span className="text-[11px] font-black uppercase tracking-[0.3em] text-[#0068B3] mb-2 block">Local Service</span>
              <h2 className="font-display font-black text-[#071126] text-[18px]">Areas We Serve</h2>
            </div>
            <div className="flex-1">
              <p className="text-gray-500 text-[14px] mb-4">
                We assist job-seeker visa applicants targeting roles across <strong className="text-[#071126]">Abu Dhabi, Dubai, and the Northern Emirates</strong> — from our Mussafah office.
              </p>
              <div className="flex flex-wrap gap-2">
                {['Mussafah', 'Mohammed Bin Zayed City', 'Khalifa City', 'Al Nahyan', 'Abu Dhabi City', 'Al Ain', 'Dubai', 'Sharjah', 'Northern Emirates'].map(area => (
                  <span key={area} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#EAF7FF] border border-[#C3DCF5] text-[12px] font-black text-[#0068B3]">
                    <MapPin size={11} weight="fill" />{area}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default function JobSeekerVisa() {
  return (
    <VisaDetailLayout
      visa={VISA_BY_ID['job-seeker-visa']}
      extraSections={<JobSeekerVisaExtraSections />}
    />
  );
}
